import { useEffect, useRef, useState } from 'react';
import { getNoteForTrigger, type ResearchNote } from '@/lib/researchNotes';
import { useNavigate } from 'react-router-dom';
import { Sparkles, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/lib/supabase';
import { useTelegram } from '@/hooks/useTelegram';
import { useWater } from '@/hooks/useWater';
import { useDailyMicronutrients } from '@/hooks/useMicronutrients';

/**
 * Строка Славы на главном экране — ОДНА фраза-действие по данным дня.
 *
 * Зачем: Слава жил только во вкладке, и к нему надо было идти. Замерено
 * 03.09.2026: за 30 дней ему писали 4 человека из 43. Здесь он встречает
 * человека там, где тот и так находится, — но по правилам «хартии
 * ненавязчивости» (решение Славы 03.09.2026):
 *   · не окно поверх экрана, а строка в потоке; главное действие не перекрыто;
 *   · одна реплика на экран;
 *   · «сказал — замолчал»: три показа без ответа → раз в неделю (считает база);
 *   · «помолчи» — выключается одним тапом и не возвращается сама;
 *   · молчит, когда сказать нечего: null вместо дежурной фразы.
 *
 * Фраза собирается на клиенте из того, что на экране уже есть: макросы,
 * вода, шаги, микронутриенты. Без модели — это показ на каждом открытии
 * главной, и модель здесь стоила бы денег и ожидания на ровном месте.
 * Модель подключается ТАПОМ: «спросить» уводит в чат с этой же фразой
 * как первой репликой — контекст уже загружен.
 */

type FirstDays = {
  day: number;
  today_meals: number;
  today_photos: number;
  total_meals: number;
  total_photos: number;
};

/**
 * Цель первых трёх дней (решение Славы 04.09.2026).
 *
 * До сих пор её не было вовсе: онбординг заканчивался, человека выбрасывало
 * на главную, и дальше он оставался сам по себе. А по данным беты видно,
 * что решает именно плотность старта: у оставшихся 16.4 приёма за первые
 * 48 часов, у ушедших — 3.6. Поэтому пробный период НЕ удлиняем (лишние дни
 * не превращают вялого в активного, только отодвигают стену), а даём
 * конкретную цель на каждый из трёх дней с видимым прогрессом.
 *
 * Возвращает null, когда цель на сегодня уже закрыта, — по хартии
 * ненавязчивости Слава молчит, когда сказать нечего.
 */
function firstDaysGoal(f: FirstDays | null): { text: string; ask: string } | null {
  if (!f || f.day > 3) return null;

  if (f.day === 1) {
    const left = 3 - f.today_meals;
    if (left > 0) {
      return {
        text: f.today_meals === 0
          ? 'Запиши три приёма сегодня — этого хватит, чтобы я понял твой рацион'
          : `Ещё ${left} ${left === 1 ? 'приём' : 'приёма'} — и я пойму твой рацион (${f.today_meals} из 3)`,
        ask: 'Я только начал. Что мне важно записывать в первые дни?',
      };
    }
    return { text: 'Три приёма записаны. Завтра покажу, что я заметил', ask: 'Что ты уже заметил по моим записям?' };
  }

  if (f.day === 2) {
    // Кто пришёл текстом, не пробовал главную фичу — а она и продаёт.
    if (f.total_photos === 0) {
      return {
        text: 'Попробуй сфотографировать тарелку — это быстрее, чем печатать',
        ask: 'Насколько точно ты считаешь КБЖУ по фото?',
      };
    }
    if (f.today_meals === 0) {
      return { text: 'Второй день. Запиши первый приём — серия не прервётся', ask: 'Как не бросить на второй день?' };
    }
    return null;
  }

  // День 3 — стена совсем близко, и это момент подъёма: серия вот-вот замкнётся
  if (f.today_meals === 0) {
    return { text: 'Третий день. Запиши приём — и серия из трёх дней твоя', ask: 'Что даёт регулярность записей?' };
  }
  return { text: `Три дня подряд — почти готово. Записано ${f.total_meals}`, ask: 'Разбери, что у меня получилось за три дня' };
}

type Props = {
  totals: { calories: number; protein: number; fat: number; carbs: number };
  targets: { calories: number; protein: number; fat: number; carbs: number };
  steps: number;
};

// Чем добрать конкретный нутриент — коротко, продуктом, а не лекцией.
const LACK_HINT: Record<string, string> = {
  vegetables_fruits: 'добавь что-то зелёное к следующему приёму',
  fiber: 'овощи или крупа к следующему приёму закроют клетчатку',
  protein: 'начни следующий приём с творога или яиц',
  omega3: 'рыба или горсть орехов сегодня — и омега закрыта',
  calcium: 'творог или сыр добавят кальция',
  iron: 'гречка или красное мясо добавят железа',
  magnesium: 'орехи или банан — и магний на месте',
  zinc: 'мясо, яйца или тыквенные семечки добавят цинка',
  potassium: 'банан или печёная картошка добавят калия',
  vitamin_d: 'витамин D сегодня почти ноль — яйца или жирная рыба',
  vitamin_b12: 'B12 сегодня почти ноль — яйца, рыба или молочное',
  vitamin_c: 'витамина C мало — любой фрукт или перец',
};

/**
 * Реплика на главной и вопрос, который уходит в чат по «спросить» (14.10.2026).
 *
 * Раньше в поле чата подставлялась САМА реплика Дофи — «Воды пока половина нормы —
 * стакан прямо сейчас» — и уходила как будто от человека. Разборщик читал «стакан»
 * как запись еды и записывал перекус «вода 250 г, 0 ккал» (боевой случай Славы).
 * Теперь у каждой реплики свой вопрос — от лица человека и без слов, похожих на запись.
 */
type Phrase = { text: string; ask: string };

function buildPhrase(p: Props, waterPct: number, lack: { key: string; label: string } | null): Phrase | null {
  const h = new Date().getHours();
  const proteinPct = p.targets.protein > 0 ? p.totals.protein / p.targets.protein : 1;
  const hasMeals = p.totals.calories > 0;

  // Утром до первой еды говорить не о чем — данных нет, а «сфотографируй еду»
  // и так написано на кнопке ниже.
  if (!hasMeals && h < 12) return null;

  if (hasMeals && proteinPct < 0.5 && h >= 14) {
    return {
      text: `Белка ${Math.round(p.totals.protein)} из ${Math.round(p.targets.protein)} — начни ужин с творога или яиц`,
      ask: 'Белка сегодня мало. Чем добрать его к ужину?',
    };
  }
  if (hasMeals && lack && LACK_HINT[lack.key]) {
    const veg = lack.key === 'vegetables_fruits';
    const who = veg ? 'Сегодня почти без овощей' : `Не хватает: ${lack.label.toLowerCase()}`;
    return {
      text: `${who} — ${LACK_HINT[lack.key]}`,
      ask: veg ? 'Как добавить овощей в мой день?' : `Мне не хватает: ${lack.label.toLowerCase()}. Чем его добрать?`,
    };
  }
  if (hasMeals && proteinPct < 0.5) {
    return {
      text: `Белка пока ${Math.round(p.totals.protein)} из ${Math.round(p.targets.protein)} — к обеду добери`,
      ask: 'Как мне добрать белок к обеду?',
    };
  }
  if (h >= 15 && waterPct < 0.5) {
    return {
      text: 'Воды пока половина нормы — стакан прямо сейчас',
      ask: 'Почему мне важно добирать воду и как не забывать пить?',
    };
  }
  if (h >= 18 && p.steps === 0) {
    return {
      text: 'Шаги сегодня не отмечены — сколько прошёл?',
      ask: 'Как добрать шаги вечером, если день был сидячий?',
    };
  }
  if (hasMeals && proteinPct >= 0.8 && waterPct >= 0.7 && !lack) {
    return {
      text: 'Сегодня всё ровно — белок, вода, овощи на месте. Так держать',
      ask: 'Что помогает удерживать такие ровные дни?',
    };
  }
  return null;
}

/**
 * Реплика с исследованием (29.09.2026, просьба Славы: «Дофи иногда озвучивает —
 * отличная активность, учёные говорят, что даже малость полезна»).
 *
 * Правила, чтобы это не стало баннером:
 *   · только по СОБЫТИЮ с настоящим числом человека — нет числа, нет реплики;
 *   · сначала похвала за факт, потом наука (обратный порядок читается как лекция);
 *   · не перебивает то, что важнее: если в дне есть что поправить, говорим про это.
 * Тексты и источники — таблица research_notes (миграция 054).
 */
const SCIENCE_SEEN_KEY = 'science_note_seen';
const SCIENCE_REPEAT_DAYS = 60;

function scienceTrigger(steps: number): string | null {
  // Порог 8000 — из той же работы, на которую ссылается заметка: именно там
  // кривая пользы выходит на плато. Событие настоящее, а не выдуманный повод.
  if (steps >= 8000) return 'steps_day';
  if (steps >= 4000) return 'activity_up';
  return null;
}

function scienceAllowed(id: string): boolean {
  try {
    const raw = localStorage.getItem(SCIENCE_SEEN_KEY);
    const seen = raw ? JSON.parse(raw) : {};
    const last = seen[id];
    if (!last) return true;
    return (Date.now() - last) > SCIENCE_REPEAT_DAYS * 86400_000;
  } catch {
    return true;
  }
}

function markScienceSeen(id: string) {
  try {
    const raw = localStorage.getItem(SCIENCE_SEEN_KEY);
    const seen = raw ? JSON.parse(raw) : {};
    seen[id] = Date.now();
    localStorage.setItem(SCIENCE_SEEN_KEY, JSON.stringify(seen));
  } catch {
    // Память о показе — удобство, а не состояние: не сохранилось, покажем ещё раз.
  }
}

export default function SlavaLine(props: Props) {
  const nav = useNavigate();
  const { haptic } = useTelegram();
  const water = useWater();
  const { nutrients } = useDailyMicronutrients();
  const [visible, setVisible] = useState(false);
  const [firstDays, setFirstDays] = useState<FirstDays | null>(null);
  const [science, setScience] = useState<ResearchNote | null>(null);
  const shownRef = useRef(false);

  useEffect(() => {
    supabase.rpc('get_first_days').then(({ data }) => {
      if (data && typeof data === 'object' && (data as any).day) setFirstDays(data as FirstDays);
    });
  }, []);

  // Заметка исследования под сегодняшнее событие
  useEffect(() => {
    const trigger = scienceTrigger(props.steps);
    if (!trigger) return;
    getNoteForTrigger(trigger).then((n) => {
      if (n && scienceAllowed(n.id)) setScience(n);
    });
  }, [props.steps]);

  // Состояние из базы: включено ли, не пора ли замолчать
  useEffect(() => {
    supabase.rpc('slava_home', { kind: 'get' }).then(({ data }) => {
      if (data && typeof data === 'object' && (data as any).visible) setVisible(true);
    });
  }, []);

  const waterPct = water.goal > 0 ? water.total / water.goal : 1;
  const lackList = (nutrients || [])
    .filter((n: any) => n.status === 'lack' && n.target_type !== 'reverse')
    .sort((a: any, b: any) => a.percentage - b.percentage);
  const lack = lackList.length > 0 ? { key: String(lackList[0].key), label: String(lackList[0].label) } : null;

  // Первые три дня цель важнее любой обычной реплики: она и есть работа
  // этого периода. Дальше — обычная фраза по данным дня.
  const goal = firstDaysGoal(firstDays);
  const built = goal ? null : buildPhrase(props, waterPct, lack);
  const routine = goal ? goal.text : built?.text ?? null;

  // Наука уступает дорогу всему, что человеку СЕЙЧАС полезнее: цели первых дней
  // и подсказкам по дню. Она занимает место только там, где иначе было бы
  // молчание или дежурное «всё ровно».
  const routineIsFiller = !routine || routine.startsWith('Сегодня всё ровно');
  const useScience = !goal && science && routineIsFiller;
  const phrase = useScience ? science!.hook : routine;
  const askText = useScience
    ? 'Расскажи подробнее про это исследование'
    : (goal ? goal.ask : built?.ask ?? null);

  useEffect(() => {
    if (useScience && science) markScienceSeen(science.id);
  }, [useScience, science]);

  // Показ засчитывается один раз за монтирование и только когда есть что сказать:
  // молчание не должно тратить лимит «три показа без ответа».
  useEffect(() => {
    if (visible && phrase && !shownRef.current) {
      shownRef.current = true;
      supabase.rpc('slava_home', { kind: 'shown' });
    }
  }, [visible, phrase]);

  if (!visible || !phrase) return null;

  const ask = () => {
    haptic('light');
    supabase.rpc('slava_home', { kind: 'tap' });
    // Фраза уходит в чат первой репликой — LiveChat подхватывает её из sessionStorage
    try { if (askText) sessionStorage.setItem('slava_prefill', askText); } catch {}
    nav('/chat');
  };

  const hush = () => {
    haptic('medium');
    setVisible(false);
    supabase.rpc('slava_home', { kind: 'off' });
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, height: 0 }}
        className="card mb-4 flex items-center gap-2.5 pl-3 pr-2 py-2 min-h-[44px]"
        style={{ borderColor: 'rgba(16,229,142,0.28)', boxShadow: '0 0 18px rgba(16,229,142,0.10)' }}
      >
        <div className="w-[26px] h-[26px] rounded-full bg-emerald-glow/15 flex items-center justify-center shrink-0">
          <Sparkles size={15} className="text-emerald-glow" />
        </div>
        <button onClick={ask} className="flex-1 min-w-0 text-left text-[13px] leading-snug text-ink">
          {phrase}
        </button>
        <button
          onClick={ask}
          className="shrink-0 px-2.5 py-1.5 rounded-lg text-xs font-medium text-emerald-glow border border-emerald-glow/35"
        >
          спросить
        </button>
        <button onClick={hush} aria-label="Помолчи" className="shrink-0 w-8 h-8 flex items-center justify-center text-ink-dim">
          <X size={14} />
        </button>
      </motion.div>
    </AnimatePresence>
  );
}
